import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, ShieldCheck } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { formatBrasilia } from '../../../shared/lib/date';
import { FEATURES } from '../../../shared/lib/permissions';
import { SalarioColaboradorModal } from '../../usuarios/components/SalarioColaboradorModal';
import { useSalarios } from '../../usuarios/hooks';
import { Bond, Collaborator, corpoDoErro, mensagemDeErro } from '../api';
import { BondAction, BondCard } from '../components/BondCard';
import { BondFormModal } from '../components/BondFormModal';
import { CancelBondModal } from '../components/CancelBondModal';
import { AfastamentosTab } from '../components/AfastamentosTab';
import { DadosBancariosTab } from '../components/DadosBancariosTab';
import { DesligamentoTab } from '../components/DesligamentoTab';
import { FeriasTab } from '../components/FeriasTab';
import { AlocacoesTab } from '../components/AlocacoesTab';
import { DocumentosTab } from '../components/DocumentosTab';
import { EditPersonModal } from '../components/EditPersonModal';
import { EnderecosContatosTab } from '../components/EnderecosContatosTab';
import { OrganizacaoTab } from '../components/OrganizacaoTab';
import { StatusChangeModal } from '../components/StatusChangeModal';
import {
  BOND_STATUS_LABELS, EDUCATION_OPTIONS, HISTORY_EVENT_LABELS, MARITAL_OPTIONS, SEX_OPTIONS, formatDate, labelOf,
} from '../components/format';
import { PayrollModal } from '../components/PayrollModal';
import { BondStatusBadge, PersonStatusBadge } from '../components/StatusBadge';
import {
  PESSOAS_KEYS, useAtivarVinculo, useColaborador, useConfirmarVinculo, useExportarAdmissao, useHistoricoVinculo,
  useValidarColaborador,
} from '../hooks';

type TabKey = 'resumo' | 'pessoais' | 'vinculos' | 'organizacao' | 'afastamentos' | 'ferias' | 'alocacoes' | 'desligamento' | 'documentos' | 'enderecos' | 'bancarios' | 'remuneracao' | 'historico';

function Campo({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900">{value || '—'}</dd>
    </div>
  );
}

/** Vínculo vigente: o aberto, senão o mais recente (a lista já vem do mais recente). */
function vinculoVigente(person: Collaborator): Bond | undefined {
  return person.bonds.find((b) => !b.closed) ?? person.bonds[0];
}

export default function ColaboradorDetalhePage() {
  const { id = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const colaborador = useColaborador(id);
  const person = colaborador.data;

  const [tab, setTab] = useState<TabKey>('resumo');
  const [editPerson, setEditPerson] = useState(false);
  const [bondForm, setBondForm] = useState<{ open: boolean; bond: Bond | null }>({ open: false, bond: null });
  const [payroll, setPayroll] = useState<{ bond: Bond | null; mode: 'registrar' | 'conciliar' }>({ bond: null, mode: 'registrar' });
  const [cancelling, setCancelling] = useState<Bond | null>(null);
  const [changingStatus, setChangingStatus] = useState<Bond | null>(null);
  const [salarios, setSalarios] = useState(false);
  const [actionError, setActionError] = useState<{ message: string; pendencias?: Array<{ message: string }> } | null>(null);

  const validar = useValidarColaborador(id);
  const exportar = useExportarAdmissao();
  const confirmar = useConfirmarVinculo();
  const ativar = useAtivarVinculo();

  // `?novoVinculo=1`: vindo da checagem de duplicidade do cadastro.
  useEffect(() => {
    if (searchParams.get('novoVinculo') === '1' && person) {
      setTab('vinculos');
      if (!person.bonds.some((b) => !b.closed)) setBondForm({ open: true, bond: null });
      searchParams.delete('novoVinculo');
      setSearchParams(searchParams, { replace: true });
    }
  }, [person, searchParams, setSearchParams]);

  const tabs = useMemo(() => {
    const all: Array<{ key: TabKey; label: string; show: boolean }> = [
      { key: 'resumo', label: 'Resumo', show: true },
      { key: 'pessoais', label: 'Dados pessoais', show: true },
      { key: 'vinculos', label: 'Vínculos', show: true },
      {
        key: 'organizacao', label: 'Organização',
        show: can(FEATURES.pessoasLotacoes, 'view') || can(FEATURES.pessoasMovimentacoes, 'view'),
      },
      { key: 'afastamentos', label: 'Afastamentos', show: can(FEATURES.pessoasAfastamentos, 'view') },
      { key: 'ferias', label: 'Férias', show: can(FEATURES.pessoasFerias, 'view') },
      { key: 'alocacoes', label: 'Alocações', show: can(FEATURES.pessoasAlocacoes, 'view') },
      {
        key: 'desligamento', label: 'Desligamento',
        show: can(FEATURES.pessoasDesligamentos, 'view') || can(FEATURES.pessoasReintegracoes, 'view'),
      },
      { key: 'documentos', label: 'Documentos', show: can(FEATURES.pessoasDocumentos, 'view') },
      { key: 'enderecos', label: 'Endereços e contatos', show: can(FEATURES.pessoasEnderecosContatos, 'view') },
      { key: 'bancarios', label: 'Dados bancários', show: can(FEATURES.pessoasDadosBancarios, 'view') },
      // A aba não é renderizada sem permissão (EPIC 5.1).
      { key: 'remuneracao', label: 'Remuneração', show: can(FEATURES.pessoasSalarios, 'view') },
      { key: 'historico', label: 'Histórico', show: true },
    ];
    return all.filter((t) => t.show);
  }, [can]);

  function sucesso(message: string) {
    setActionError(null);
    toast.success(message);
  }

  function falha(err: unknown, fallback: string) {
    const corpo = corpoDoErro(err);
    setActionError({ message: mensagemDeErro(err, fallback), pendencias: corpo.pendencias });
  }

  async function onBondAction(action: BondAction, bond: Bond) {
    setActionError(null);
    try {
      switch (action) {
        case 'editar':
          return setBondForm({ open: true, bond });
        case 'cancelar':
          return setCancelling(bond);
        case 'situacao':
          return setChangingStatus(bond);
        case 'registrar-folha':
          return setPayroll({ bond, mode: 'registrar' });
        case 'conciliar':
          return setPayroll({ bond, mode: 'conciliar' });
        case 'exportar': {
          const filename = await exportar.mutateAsync(bond.id);
          return sucesso(`Arquivo ${filename} gerado. Envie à contabilidade.`);
        }
        case 'confirmar':
          await confirmar.mutateAsync(bond.id);
          return sucesso('Admissão confirmada.');
        case 'ativar': {
          const ativo = await ativar.mutateAsync(bond.id);
          return sucesso(`Vínculo ativado: ${BOND_STATUS_LABELS[ativo.status]}.`);
        }
      }
    } catch (err) {
      falha(err, 'Não foi possível concluir a operação.');
    }
  }

  async function validarPessoa() {
    try {
      await validar.mutateAsync();
      sucesso('Dados pessoais validados.');
    } catch (err) {
      falha(err, 'Não foi possível validar os dados pessoais.');
    }
  }

  if (colaborador.isLoading) {
    return <PageContainer title="Colaborador"><p className="text-sm text-gray-500">Carregando…</p></PageContainer>;
  }
  if (colaborador.isError || !person) {
    return (
      <PageContainer title="Colaborador" breadcrumbs={[{ label: 'Pessoas' }, { label: 'Colaboradores', to: '/pessoas/colaboradores' }]}>
        <Alert variant="error">{mensagemDeErro(colaborador.error, 'Colaborador não encontrado.')}</Alert>
      </PageContainer>
    );
  }

  const vigente = vinculoVigente(person);
  const temVinculoAberto = person.bonds.some((b) => !b.closed);
  const busy = exportar.isPending || confirmar.isPending || ativar.isPending;

  return (
    <PageContainer
      title={person.socialName || person.name}
      subtitle={vigente ? `Matrícula ${vigente.internalRegistration} · ${vigente.company?.tradeName ?? ''}` : undefined}
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Colaboradores', to: '/pessoas/colaboradores' }, { label: person.name }]}
      action={
        <Can feature={FEATURES.pessoasColaboradores} action="edit">
          <Button variant="secondary" onClick={() => setEditPerson(true)}>
            <Pencil className="mr-2 h-4 w-4" /> Editar dados pessoais
          </Button>
        </Can>
      }
    >
      {actionError && (
        <Alert variant="error" title={actionError.message}>
          {actionError.pendencias && (
            <ul className="list-disc pl-5">
              {actionError.pendencias.map((p) => <li key={p.message}>{p.message}</li>)}
            </ul>
          )}
        </Alert>
      )}

      <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex overflow-x-auto border-b border-gray-200">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap px-4 py-3 text-sm font-medium ${
                tab === t.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="space-y-4 p-5">
          {tab === 'resumo' && (
            <>
              <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Campo label="Nome civil" value={person.name} />
                <Campo label="CPF" value={person.cpf} />
                <Campo label="Situação da pessoa" value={<PersonStatusBadge status={person.personStatus} />} />
                <Campo label="Situação do vínculo" value={vigente ? <BondStatusBadge status={vigente.status} /> : '—'} />
                <Campo label="Empresa" value={vigente?.company?.tradeName} />
                <Campo label="Tipo de vínculo" value={vigente?.employmentType.toUpperCase()} />
                <Campo label="Admissão" value={formatDate(vigente?.admissionDate)} />
                <Campo label="E-mail" value={person.email} />
              </dl>
              <div className="rounded-md bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">Completude cadastral: {person.completeness.percent}%</p>
                  {person.personStatus !== 'validada' && (
                    <Can feature={FEATURES.pessoasColaboradores} action="edit">
                      <Button size="sm" onClick={validarPessoa} loading={validar.isPending}>
                        <ShieldCheck className="mr-1 h-4 w-4" /> Validar dados pessoais
                      </Button>
                    </Can>
                  )}
                </div>
                <div className="mt-2 h-2 w-full rounded bg-gray-200">
                  <div className="h-2 rounded bg-blue-600" style={{ width: `${person.completeness.percent}%` }} />
                </div>
                {person.completeness.missing.length > 0 && (
                  <p className="mt-2 text-xs text-gray-600">
                    Pendente: {person.completeness.missing.map((m) => m.label).join(', ')}.
                  </p>
                )}
              </div>
            </>
          )}

          {tab === 'pessoais' && (
            <dl className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <Campo label="Nome completo" value={person.name} />
              <Campo label="Nome social" value={person.socialName} />
              <Campo label="Nome preferencial" value={person.preferredName} />
              <Campo label="CPF" value={person.cpf} />
              <Campo label="RG" value={person.rg} />
              <Campo label="Data de nascimento" value={formatDate(person.birthDate)} />
              <Campo label="País de nascimento" value={person.birthCountry} />
              <Campo label="Nacionalidade" value={person.nationality} />
              <Campo label="Naturalidade" value={person.birthCity ? `${person.birthCity.name}/${person.birthCity.uf}` : null} />
              <Campo label="Sexo cadastral" value={labelOf(SEX_OPTIONS, person.sex)} />
              <Campo label="Estado civil" value={labelOf(MARITAL_OPTIONS, person.maritalStatus)} />
              <Campo label="Escolaridade" value={labelOf(EDUCATION_OPTIONS, person.educationLevel)} />
              <Campo label="Nome da mãe" value={person.motherName} />
              <Campo label="Nome do pai" value={person.fatherName} />
              <Campo label="E-mail" value={person.email} />
              <Campo label="Telefone" value={person.phone} />
            </dl>
          )}

          {tab === 'vinculos' && (
            <>
              {!temVinculoAberto && (
                <Can feature={FEATURES.pessoasColaboradores} action="create">
                  <div className="flex justify-end">
                    <Button onClick={() => setBondForm({ open: true, bond: null })}>
                      <Plus className="mr-2 h-4 w-4" /> Novo vínculo
                    </Button>
                  </div>
                </Can>
              )}
              {person.bonds.map((b) => <BondCard key={b.id} bond={b} busy={busy} onAction={onBondAction} />)}
            </>
          )}

          {tab === 'organizacao' && <OrganizacaoTab person={person} />}

          {tab === 'afastamentos' && <AfastamentosTab person={person} />}

          {tab === 'ferias' && <FeriasTab person={person} />}

          {tab === 'alocacoes' && <AlocacoesTab person={person} />}

          {tab === 'desligamento' && <DesligamentoTab person={person} />}

          {tab === 'documentos' && <DocumentosTab person={person} />}

          {tab === 'enderecos' && (
            <EnderecosContatosTab
              owner={{ kind: 'dp', personId: person.id }}
              canEdit={can(FEATURES.pessoasEnderecosContatos, 'create')}
              canApprove={can(FEATURES.pessoasAlteracoesCadastrais, 'edit')}
            />
          )}

          {tab === 'bancarios' && (
            <DadosBancariosTab
              owner={{ kind: 'dp', personId: person.id }}
              canCreate={can(FEATURES.pessoasDadosBancarios, 'create')}
              canEdit={can(FEATURES.pessoasDadosBancarios, 'edit')}
              canValidate={can(FEATURES.pessoasDadosBancariosValidacao, 'edit')}
            />
          )}

          {tab === 'remuneracao' && <RemuneracaoTab person={person} onManage={() => setSalarios(true)} />}

          {tab === 'historico' && <HistoricoTab person={person} />}
        </div>
      </section>

      <EditPersonModal open={editPerson} person={person} onClose={() => setEditPerson(false)}
        onSaved={(m) => { setEditPerson(false); sucesso(m); }} />
      <BondFormModal open={bondForm.open} personId={person.id} bond={bondForm.bond}
        onClose={() => setBondForm({ open: false, bond: null })}
        onSaved={(m) => { setBondForm({ open: false, bond: null }); sucesso(m); }} />
      <PayrollModal bond={payroll.bond} mode={payroll.mode} onClose={() => setPayroll((p) => ({ ...p, bond: null }))}
        onSaved={(m) => { setPayroll((p) => ({ ...p, bond: null })); sucesso(m); }} />
      <StatusChangeModal bond={changingStatus} onClose={() => setChangingStatus(null)}
        onDone={(m) => { setChangingStatus(null); sucesso(m); }} />
      <CancelBondModal bond={cancelling} onClose={() => setCancelling(null)}
        onSaved={(m) => { setCancelling(null); sucesso(m); }} />
      {salarios && (
        <SalarioColaboradorModal
          colaborador={{ id: person.id, name: person.name }}
          onClose={() => {
            setSalarios(false);
            // O salário muda as pendências do vínculo CLT.
            qc.invalidateQueries({ queryKey: PESSOAS_KEYS.all });
          }}
        />
      )}
    </PageContainer>
  );
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

function RemuneracaoTab({ person, onManage }: { person: Collaborator; onManage: () => void }) {
  const salarios = useSalarios(person.id);
  const lista = salarios.data ?? [];
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button variant="secondary" onClick={onManage}>Gerenciar salários</Button>
      </div>
      {salarios.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : lista.length === 0 ? (
        <EmptyState size="compact" title="Nenhum salário cadastrado" description="Vínculo CLT precisa de salário vigente na admissão." />
      ) : (
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="text-left text-xs uppercase text-gray-500">
            <tr><th className="py-2">Vigente a partir de</th><th className="py-2">Valor</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {lista.map((s) => (
              <tr key={s.id}>
                <td className="py-2">{s.validFrom.split('-').reverse().join('/')}</td>
                <td className="py-2">{formatCurrency(s.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function HistoricoTab({ person }: { person: Collaborator }) {
  const [bondId, setBondId] = useState(vinculoVigente(person)?.id);
  const historico = useHistoricoVinculo(bondId);
  return (
    <div className="space-y-4">
      {person.bonds.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {person.bonds.map((b) => (
            <Button key={b.id} size="sm" variant={b.id === bondId ? 'primary' : 'secondary'} onClick={() => setBondId(b.id)}>
              Matrícula {b.internalRegistration}
            </Button>
          ))}
        </div>
      )}
      {historico.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : (
        <ol className="space-y-3 border-l border-gray-200 pl-4">
          {(historico.data ?? []).map((h) => (
            <li key={h.id}>
              <p className="text-sm font-medium text-gray-900">
                {HISTORY_EVENT_LABELS[h.event] ?? h.event}
                {h.toStatus && h.fromStatus !== h.toStatus && (
                  <span className="font-normal text-gray-600">
                    {' '}— {h.fromStatus ? `${BOND_STATUS_LABELS[h.fromStatus as keyof typeof BOND_STATUS_LABELS] ?? h.fromStatus} → ` : ''}
                    {BOND_STATUS_LABELS[h.toStatus as keyof typeof BOND_STATUS_LABELS] ?? h.toStatus}
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500">{formatBrasilia(h.createdAt)}</p>
              {h.event === 'payroll_divergence' && h.payload && (
                <p className="text-xs text-amber-700">
                  Registrado: {String(h.payload.valorInterno)} · Recebido: {String(h.payload.valorExterno)}
                </p>
              )}
              {h.justification && <p className="text-xs text-gray-600">Justificativa: {h.justification}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
